import { EventEmitter } from './event-emitter'
// @ts-expect-error - splatv module doesn't have TypeScript definitions
import { SplatViewer } from '../../lib/splatv/splatv-module.js'
// @ts-expect-error - camera controls module doesn't have TypeScript definitions
import { CameraControls } from '../../lib/splatv/camera-controls.js'
import type { CameraState } from '@/types/camera'
import type { Quaternion } from '@/utils/quaternion'
import { cameraStateToQuaternion } from '@/utils/cameraConversion'

interface SplatRendererOptions {
  quality?: 'low' | 'medium' | 'high'
  autoRotate?: boolean
  controls?: boolean
  controlMode?: 'orbit' | 'free'
  defaultCamera?: CameraState
  autoFocus?: boolean
}

export class SplatRenderer extends EventEmitter {
  private viewer!: SplatViewer
  private animationId: number | null = null
  private fps = 0
  private fpsFrames = 0
  private fpsTime = 0
  private isLoading = false
  private autoRotate: boolean
  private controls: CameraControls | null = null
  private controlMode: 'orbit' | 'free' = 'orbit'
  private controlsEnabled = true
  private hasInitializedControls = false
  private useCameraControls = true // Use 4dgs-stgs_viewer style camera controls
  public isSettingCamera = false
  cameraPosition = [0, 0, 5]
  cameraQuaternion = { w: 1, x: 0, y: 0, z: 0 } // Identity quaternion
  
  // Getter for camera controls
  get cameraControls() {
    return this.controls
  }
  private defaultCameraPosition: [number, number, number] = [0, 0, 5]
  private defaultCameraQuaternion: Quaternion = { w: 1, x: 0, y: 0, z: 0 }
  private autoFocus: boolean = true
  private options: SplatRendererOptions

  canvas: HTMLCanvasElement

  constructor(canvas: HTMLCanvasElement, options: SplatRendererOptions = {}) {
    super()
    this.canvas = canvas
    this.options = options
    this.autoRotate = options.autoRotate || false
    this.controlMode = options.controlMode || 'orbit'
    this.controlsEnabled = options.controls !== false
    this.autoFocus = options.autoFocus !== false
    
    // Set default camera if provided
    if (options.defaultCamera) {
      const { position, quaternion } = cameraStateToQuaternion(options.defaultCamera)
      this.cameraPosition = position
      this.cameraQuaternion = quaternion
      this.defaultCameraPosition = [...this.cameraPosition] as [number, number, number]
      this.defaultCameraQuaternion = { ...this.cameraQuaternion }
    } else {
    }
    
    try {
      this.viewer = new SplatViewer(canvas)
      
      // Don't initialize controls immediately - wait for model load
      // This prevents controls from overriding the default camera
      
      this.startRenderLoop()
    } catch (error) {
      console.error('Failed to create SplatViewer:', error)
      this.emit('error', error)
    }
  }

  private initializeControls() {
    
    // Pass renderer reference for event emission BEFORE creating controls
    // This ensures the CameraControls constructor can emit events properly
    this.viewer.renderer = this
    
    // Create initial view matrix if we have default camera
    let initialViewMatrix = null
    if (this.options.defaultCamera) {
      initialViewMatrix = this.createViewMatrixFromPositionQuaternion(
        this.cameraPosition as [number, number, number],
        this.cameraQuaternion
      )
    }
    
    // Temporarily clear isSettingCamera to allow initial emit
    const wasSettingCamera = this.isSettingCamera
    this.isSettingCamera = false
    
    // Always use the new camera-controls.js from 4dgs-stgs_viewer approach
    this.controls = new CameraControls(this.canvas, this.viewer, initialViewMatrix)
    
    // Restore the flag
    this.isSettingCamera = wasSettingCamera
    
    // Listen for camera changes from controls
    // This replaces the continuous polling in the render loop
    this.on('cameraChange', (...args: unknown[]) => {
      const position = args[0] as [number, number, number]
      const quaternion = args[1] as Quaternion
      this.cameraPosition = [...position]
      this.cameraQuaternion = { ...quaternion }
    })
    
    // No need to force update - CameraControls handles initialization properly
  }

  public toggleControlMode() {
    // With unified camera controls, we don't actually switch control implementations
    // Instead, we just toggle a mode flag that affects behavior
    this.controlMode = this.controlMode === 'orbit' ? 'free' : 'orbit'
    
    // Emit event so UI can update
    this.emit('controlModeChanged', this.controlMode)
  }

  public getControlMode() {
    return this.controlMode
  }
  
  autoFocusCamera() {
    const bounds = this.viewer.getBounds()
    if (!bounds) {
      console.warn('No bounds available for auto-focus')
      return
    }
    
    const { center, size } = bounds
    
    // Calculate the maximum dimension
    const maxDim = Math.max(...size)
    
    // Calculate optimal camera distance
    // Use a FOV of 45 degrees, add some padding (1.5x)
    const fov = 45 * (Math.PI / 180)
    const distance = (maxDim * 1.5) / (2 * Math.tan(fov / 2))
    
    // Position camera looking at the center
    const cameraPos: [number, number, number] = [
      center[0],
      center[1],
      center[2] + distance
    ]
    
    // Store the auto-focused position as the default
    this.defaultCameraPosition = cameraPos
    this.defaultCameraQuaternion = { w: 1, x: 0, y: 0, z: 0 } // Identity quaternion for front view
    
    // Set the camera
    this.setCameraWithQuaternion(cameraPos, this.defaultCameraQuaternion)
  }
  
  resetCamera() {
    // Reset to the default camera position
    // This will be either the user-defined defaultCamera or the auto-focused position
    this.setCameraWithQuaternion(this.defaultCameraPosition, this.defaultCameraQuaternion)
  }
  
  setModelTransform(transform: { position: { x: number; y: number; z: number }, rotation: { rx: number; ry: number; rz: number }, scale: { x: number; y: number; z: number } } | null) {
    this.viewer.setModelTransform(transform)
  }
  
  // Gizmo control methods
  setGizmoVisible(visible: boolean) {
    if (this.viewer && 'setGizmoVisible' in this.viewer) {
      this.viewer.setGizmoVisible(visible)
    }
  }
  
  setGizmoMode(mode: 'translate' | 'rotate' | 'scale') {
    if (this.viewer && 'setGizmoMode' in this.viewer) {
      this.viewer.setGizmoMode(mode)
    }
  }
  
  setGizmoPosition(position: { x: number; y: number; z: number }) {
    if (this.viewer && 'setGizmoPosition' in this.viewer) {
      this.viewer.setGizmoPosition(position)
    }
  }
  
  setGizmoScale(scale: number) {
    if (this.viewer && 'setGizmoScale' in this.viewer) {
      this.viewer.setGizmoScale(scale)
    }
  }
  
  intersectGizmo(mouseX: number, mouseY: number): string | null {
    if (this.viewer && 'intersectGizmo' in this.viewer) {
      return this.viewer.intersectGizmo(mouseX, mouseY)
    }
    return null
  }

  async loadModel(url: string) {
    if (this.isLoading) return
    
    this.isLoading = true
    this.emit('progress', 0)
    
    try {
      await this.viewer.loadFile(url, (progress: number) => {
        this.emit('progress', progress)
      })
      this.isLoading = false
      
      // Auto-focus camera only if no default camera is set and autoFocus is enabled
      if (this.autoFocus && !this.options.defaultCamera) {
        this.autoFocusCamera()
      }
      
      // Always set up the projection matrix and default-camera view matrix,
      // regardless of whether user-input controls are enabled. (Previously
      // these were gated on `controlsEnabled`, which made `controls={false}`
      // render a blank canvas.)
      const applyInitialView = () => {
        if (this.useCameraControls) {
          this.viewer.updateProjectionMatrix(45) // Default FOV
        }
        if (this.options.defaultCamera) {
          const viewMatrix = this.createViewMatrixFromPositionQuaternion(
            this.cameraPosition as [number, number, number],
            this.cameraQuaternion
          )
          this.viewer.setViewMatrix(viewMatrix)
        } else if (!this.controlsEnabled) {
          // No controls and no defaultCamera — re-push the auto-focused view
          // so the sort worker gets the up-to-date viewProj for the current
          // canvas size.
          const viewMatrix = this.createViewMatrixFromPositionQuaternion(
            this.defaultCameraPosition,
            this.defaultCameraQuaternion
          )
          this.viewer.setViewMatrix(viewMatrix)
        }
      }
      // Apply now and again on the next two animation frames. The model load
      // sometimes completes before the canvas has its final size, which makes
      // the sort worker render with a stale viewProj. Replaying on subsequent
      // frames covers the resize race without needing controls to be enabled.
      applyInitialView()
      requestAnimationFrame(() => {
        applyInitialView()
        requestAnimationFrame(applyInitialView)
      })

      // Initialize the interactive camera controls only when enabled.
      if (this.controlsEnabled && !this.hasInitializedControls) {
        this.initializeControls()
        this.hasInitializedControls = true

        // Emit initial camera state for UI components (axis gizmo, etc.)
        // This ensures axis helper and other UI components get the correct initial state
        if (this.controls && 'getCameraState' in this.controls) {
          const state = (this.controls as CameraControls).getCameraState()
          this.emit('cameraChange', state.position, state.quaternion)
        }
      }
      
      this.emit('load')
    } catch (error) {
      this.isLoading = false
      this.emit('error', error)
    }
  }

  resize(width: number, height: number) {
    this.viewer.resize(width, height)
    // Update projection matrix on resize for new controls
    if (this.useCameraControls) {
      this.viewer.updateProjectionMatrix(45) // Default FOV
    }
  }

  setCameraWithQuaternion(position: [number, number, number], quaternion: Quaternion) {
    // Check if values actually changed to prevent unnecessary updates
    const posChanged = position.some((v, i) => Math.abs(v - this.cameraPosition[i]) > 0.0001)
    const quatChanged = 
      Math.abs(quaternion.w - this.cameraQuaternion.w) > 0.0001 ||
      Math.abs(quaternion.x - this.cameraQuaternion.x) > 0.0001 ||
      Math.abs(quaternion.y - this.cameraQuaternion.y) > 0.0001 ||
      Math.abs(quaternion.z - this.cameraQuaternion.z) > 0.0001
    
    if (!posChanged && !quatChanged) {
      return // No change, skip update
    }
    
    this.isSettingCamera = true
    this.cameraPosition = [...position]
    this.cameraQuaternion = { ...quaternion }
    
    if (this.controls && 'setCameraFromPositionQuaternion' in this.controls) {
      // Use the new method that uses quaternion directly
      ;(this.controls as CameraControls).setCameraFromPositionQuaternion(position, quaternion)
    } else if (this.controls && 'setViewMatrix' in this.controls) {
      // Fallback to view matrix conversion
      const viewMatrix = this.createViewMatrixFromPositionQuaternion(position, quaternion)
      ;(this.controls as CameraControls).setViewMatrix(viewMatrix)
    } else {
      // No controls (controls={false}) — write the view matrix straight to the
      // underlying viewer so autoFocus / defaultCamera still take effect.
      const viewMatrix = this.createViewMatrixFromPositionQuaternion(position, quaternion)
      this.viewer.setViewMatrix(viewMatrix)
    }
    
    // Don't emit camera change here - camera-controls.js already emits it in updateRenderer()
    // This prevents duplicate events
    
    // Reset flag after a short delay
    setTimeout(() => {
      this.isSettingCamera = false
    }, 50)
  }
  
  private createViewMatrixFromPositionQuaternion(position: [number, number, number], quaternion: Quaternion): Float32Array {
    const [x, y, z] = position
    const { w, x: qx, y: qy, z: qz } = quaternion
    
    // Convert quaternion to rotation matrix
    const xx = qx * qx, yy = qy * qy, zz = qz * qz
    const xy = qx * qy, xz = qx * qz, yz = qy * qz
    const wx = w * qx, wy = w * qy, wz = w * qz
    
    const r00 = 1 - 2 * (yy + zz)
    const r01 = 2 * (xy - wz)
    const r02 = 2 * (xz + wy)
    const r10 = 2 * (xy + wz)
    const r11 = 1 - 2 * (xx + zz)
    const r12 = 2 * (yz - wx)
    const r20 = 2 * (xz - wy)
    const r21 = 2 * (yz + wx)
    const r22 = 1 - 2 * (xx + yy)
    
    // View matrix - using row-major layout like 4dgs-stgs_viewer
    return new Float32Array([
      r00, r01, r02, 0,
      r10, r11, r12, 0,
      r20, r21, r22, 0,
      -(r00 * x + r10 * y + r20 * z),
      -(r01 * x + r11 * y + r21 * z),
      -(r02 * x + r12 * y + r22 * z),
      1
    ])
  }

  private startRenderLoop() {
    console.log('Starting render loop')
    const render = (time: number) => {
      // Calculate FPS
      this.fpsFrames++
      if (time - this.fpsTime >= 1000) {
        this.fps = Math.round((this.fpsFrames * 1000) / (time - this.fpsTime))
        this.fpsFrames = 0
        this.fpsTime = time
        this.emit('fps', this.fps)
        // console.log('FPS:', this.fps, 'Viewer:', this.viewer)
      }

      // Update controls
      if (this.controls) {
        // Update keyboard-based camera movement
        if ('updateFromKeyboard' in this.controls) {
          (this.controls as CameraControls).updateFromKeyboard()
        }
        
        // Note: We don't need to get camera state every frame
        // The camera-controls.js will emit 'cameraChange' events when the camera actually changes
        // This prevents unnecessary state updates and improves performance
      }

      // Auto-rotate
      if (this.autoRotate && !this.controls?.isDragging) {
        const angle = time * 0.001
        const radius = Math.sqrt(this.cameraPosition[0] ** 2 + this.cameraPosition[2] ** 2)
        this.cameraPosition[0] = Math.sin(angle) * radius
        this.cameraPosition[2] = Math.cos(angle) * radius
        // Update quaternion for Y-axis rotation
        this.cameraQuaternion = {
          w: Math.cos(angle / 2),
          x: 0,
          y: Math.sin(angle / 2),
          z: 0
        }
      }

      // Update camera - only for legacy controls
      if (!this.useCameraControls) {
        // Note: Legacy viewer.setCamera might need conversion
        // For now, we'll keep it as-is since it's legacy code
      }

      // Render
      try {
        this.viewer.render()
      } catch (e) {
        console.error('Render error in loop:', e)
      }

      // Continue loop
      this.animationId = requestAnimationFrame(render)
    }

    this.animationId = requestAnimationFrame(render)
  }

  /**
   * Stop render loop (for XR takeover)
   */
  stopRenderLoop() {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }
  
  /**
   * Restart render loop (after XR ends)
   */
  restartRenderLoop() {
    if (this.animationId === null) {
      this.startRenderLoop()
    }
  }
  
  /**
   * Get the underlying splatv viewer instance
   */
  getSplatvModule() {
    return this.viewer
  }

  dispose() {
    this.stopRenderLoop()

    if (this.controls) {
      this.controls.dispose()
    }

    this.viewer.dispose()
    this.removeAllListeners()
  }
}